import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '../app.reducer';
import { Point } from '../pages/delivery-address/shared/map/map.models';
import { Observable, of, switchMap, take } from 'rxjs';
import { YaMapsService } from './ya-maps.service';
import { IDeliveryTerminalInfo } from '../models/organizationInfo/deliveryTerminal/deliveryTerminal.model';
import { DeliveryAddress, DeliveryPoint } from '../models/address';
import { IDeliveryZone } from '../models/organizationInfo/deliveryTerminal/deliveryZone.model';
import { organizationInfoSelector } from '../store/organization.selectors';
import { getStoredState } from 'app/store/storage.meta-reducer';
import { DeliveryType } from 'app/models/user-order.model';

@Injectable({
  providedIn: 'root'
})
export class OrganizationsService {

  public deliveryTerminals: IDeliveryTerminalInfo[] = [];

  constructor(
    private store: Store<AppState>
  ) {
    this.store.select('organizationInfo').subscribe(value => {
      if (value.organizationInfo) {
        this.deliveryTerminals = value.organizationInfo.deliveryTerminals
      }
    });
  }

  getTerminalByPoint(addressId: string, point: Point): Observable<IDeliveryTerminalInfo> {
    let deliveryTerminal;
    deliveryTerminal = this.deliveryTerminals.find(deliveryTerminal => {
      // заглушка на время, если id адреса и id терминала совпадают, то это самовывоз
      if (deliveryTerminal.id === addressId) {
        return true;
      }

      let hasDeliveryZones = deliveryTerminal.deliveryZones.length;

      // if delivery terminal not exists that means every address is correct return true
      if (!hasDeliveryZones) {
        return true;
      } else {
        for (const deliveryZone of deliveryTerminal.deliveryZones) {
          if (YaMapsService.pointInsideZone(point, deliveryZone.coordinates)) {
            return true;
          }
        }
        return false;
      }
    });
    return of(deliveryTerminal ?? this.deliveryTerminals[0]);
  }

  getDeliveryZoneByTerminal(deliveryPoint: DeliveryPoint): Observable<IDeliveryZone | null> {
    return this.store.select(organizationInfoSelector).pipe(
      take(1),
      switchMap(organization => {
        const deliveryTerminals = organization?.deliveryTerminals ?? [];
        for (let deliveryTerminal of deliveryTerminals) {
          for (const deliveryZone of deliveryTerminal.deliveryZones) {
            if (YaMapsService.pointInsideZone({
              lat: deliveryPoint.latitude,
              lng: deliveryPoint.longitude
            }, deliveryZone.coordinates)
            ) {
              return of(deliveryZone);
            }
          }
        }
        return of(null);
      })
    )
  }

  getStoredState(organizationCode: string): Observable<{
    currentZone: IDeliveryZone | null
    currentAddress: DeliveryAddress | null,
  }> {
    return this.store.select('organizationInfo').pipe(
      take(1),
      switchMap((organizationInfo) => {
        if (organizationInfo.organizationInfo) {
          const localState = getStoredState(organizationCode);
          let currentAddress: DeliveryAddress | null = null;
          if (localState.currentAddress) {
            if (localState.currentAddress.type === DeliveryType.PICKUP) {
              for (let deliveryTerminal of organizationInfo.organizationInfo.deliveryTerminals) {
                if (deliveryTerminal.id === localState.currentAddress.id) {
                  currentAddress = {
                    id: deliveryTerminal.id,
                    type: DeliveryType.PICKUP,
                    addressId: null,
                    deliveryPoint: deliveryTerminal.deliveryPoint,
                    deliveryTerminal: deliveryTerminal,
                    name: deliveryTerminal.name
                  };
                  break;
                }
              }
            } else {
              if (localState.currentAddress.deliveryTerminal) {
                for (let deliveryTerminal of organizationInfo.organizationInfo.deliveryTerminals) {
                  if (deliveryTerminal.id === localState.currentAddress.deliveryTerminal.id) {
                    currentAddress = {
                      ...localState.currentAddress,
                      deliveryTerminal: deliveryTerminal
                    };
                    break;
                  }
                }
              }

            }
          }

          if (currentAddress) {
            return this.getDeliveryZoneByTerminal(currentAddress.deliveryPoint)
              .pipe(
                take(1),
                switchMap(deliveryZone => {
                  return of({
                    currentAddress: currentAddress,
                    currentZone: deliveryZone
                  });
                })
              );
          } else {
            return of({
              currentAddress: null,
              currentZone: null
            });
          }
        } else {
          return of({
            currentAddress: null,
            currentZone: null
          });
        }
      })
    );
  }
}
